import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, TrendingUp, TrendingDown, AlertTriangle, Trophy, Users, Clock, CheckCircle, Target, Bell, Award, BarChart3, Sparkles, ClipboardCheck, Star } from 'lucide-react';
import PerformanceAlerts from '../components/PerformanceAlerts';
import PerformanceBadges, { BadgesList } from '../components/PerformanceBadges';
import PerformanceTrends from '../components/PerformanceTrends';
import EmployeeEvaluation from '../components/EmployeeEvaluation';
import EmployeesAtRisk from '../components/EmployeesAtRisk';
import AdminPeerReview from '../components/AdminPeerReview';
import EmployeePeerReview from '../components/EmployeePeerReview';

const getScoreColor = (color) => {
  switch (color) {
    case 'green': return 'bg-emerald-500';
    case 'yellow': return 'bg-amber-500';
    case 'red': return 'bg-red-500';
    default: return 'bg-slate-500';
  }
};

const getScoreBadge = (color) => {
  switch (color) {
    case 'green': return <Badge className="bg-emerald-500 text-white">Excelent</Badge>;
    case 'yellow': return <Badge className="bg-amber-500 text-white">Mediu</Badge>;
    case 'red': return <Badge className="bg-red-500 text-white">Necesită atenție</Badge>;
    default: return <Badge>N/A</Badge>;
  }
};

const KPICard = ({ kpi }) => {
  return (
    <Card className="border-l-4" style={{ borderLeftColor: kpi.category_color === 'green' ? '#10b981' : kpi.category_color === 'yellow' ? '#f59e0b' : '#ef4444' }}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          {kpi.display_name}
          <span className="text-2xl font-bold">{Math.round(kpi.category_score)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {kpi.metrics.map((metric, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{metric.display_name}</span>
                <span className="font-medium">{metric.value}%</span>
              </div>
              <Progress value={metric.value} className="h-2" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const EmployeeCard = ({ employee, onClick }) => {
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => onClick(employee.employee_id)}
      data-testid={`employee-card-${employee.employee_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-semibold">{employee.employee_name}</h4>
            <p className="text-sm text-muted-foreground">{employee.employee_email}</p>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${getScoreColor(employee.overall_color)}`}>
            {Math.round(employee.overall_score)}
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
          <div>
            <p className="text-muted-foreground">Ore</p>
            <p className="font-semibold">{employee.total_hours}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Taskuri</p>
            <p className="font-semibold">{employee.tasks_completed}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Utilizare</p>
            <p className="font-semibold">{employee.utilization_pct}%</p>
          </div>
        </div>
        
        {employee.main_alert && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {employee.main_alert}
          </div>
        )}
        
        {employee.top_strength && (
          <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700 flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            {employee.top_strength}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TeamOverview = ({ report }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Scor Mediu Echipă</p>
              <p className="text-3xl font-bold">{report.avg_team_score}</p>
            </div>
            <Target className="w-10 h-10 text-blue-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Ore</p>
              <p className="text-3xl font-bold">{report.total_hours}</p>
            </div>
            <Clock className="w-10 h-10 text-purple-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Taskuri Completate</p>
              <p className="text-3xl font-bold">{report.total_tasks_completed}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Angajați</p>
              <p className="text-3xl font-bold">{report.total_employees}</p>
            </div>
            <Users className="w-10 h-10 text-orange-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StatusDistribution = ({ report }) => {
  const total = report.green_count + report.yellow_count + report.red_count;
  
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Distribuție Status Performanță</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <div className="flex h-4 rounded-full overflow-hidden">
              <div 
                className="bg-emerald-500" 
                style={{ width: `${(report.green_count / total) * 100}%` }}
              />
              <div 
                className="bg-amber-500" 
                style={{ width: `${(report.yellow_count / total) * 100}%` }}
              />
              <div 
                className="bg-red-500" 
                style={{ width: `${(report.red_count / total) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span>{report.green_count} Excelent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span>{report.yellow_count} Mediu</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>{report.red_count} Atenție</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function EmployeePerformance() {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [teamReport, setTeamReport] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const isAdmin = user?.role === 'admin' || user?.role === 'project_manager';

  // Generate month options for last 12 months
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('ro-RO', { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  useEffect(() => {
    fetchReport();
  }, [selectedMonth]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const response = await api.get(`/reports/employee-performance?month=${selectedMonth}`);
        setTeamReport(response.data);
      } else {
        const response = await api.get(`/reports/my-performance?month=${selectedMonth}`);
        setEmployeeDetail(response.data);
      }
    } catch (err) {
      console.error('Error fetching performance report:', err);
      setError(err.response?.data?.detail || 'Eroare la încărcarea raportului');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetail = async (employeeId) => {
    try {
      const response = await api.get(`/reports/employee-performance/${employeeId}?month=${selectedMonth}`);
      setEmployeeDetail(response.data);
      setSelectedEmployee(employeeId);
      setActiveTab('detail');
    } catch (err) {
      console.error('Error fetching employee detail:', err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/reports/employee-performance/export?month=${selectedMonth}&format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employee_performance_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-red-600">{error}</p>
            <Button onClick={fetchReport} className="mt-4">Încearcă din nou</Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  // Employee self-view
  if (!isAdmin && employeeDetail) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="employee-self-performance">
          {/* Header with motivational touch */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-amber-500" />
                Performanța Mea
              </h1>
              <p className="text-muted-foreground">Dashboard personal - urmărește-ți progresul</p>
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hero Score Card */}
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className={`w-28 h-28 rounded-full flex items-center justify-center text-4xl font-bold shadow-lg ${
                  employeeDetail.overall_color === 'green' ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' :
                  employeeDetail.overall_color === 'yellow' ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                  'bg-gradient-to-br from-red-400 to-red-600'
                }`}>
                  {Math.round(employeeDetail.overall_score)}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold">{employeeDetail.employee_name}</h2>
                  <p className="text-slate-300">{employeeDetail.employee_email}</p>
                  <div className="mt-3 flex items-center gap-3">
                    {getScoreBadge(employeeDetail.overall_color)}
                    <span className="text-sm text-slate-400">
                      Încredere date: {employeeDetail.confidence_score}%
                    </span>
                  </div>
                </div>
                {employeeDetail.rank_in_team && (
                  <div className="text-center bg-white/10 rounded-xl p-4">
                    <p className="text-4xl font-bold text-amber-400">#{employeeDetail.rank_in_team}</p>
                    <p className="text-sm text-slate-300">din {employeeDetail.total_team_members} colegi</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tabs for different sections */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="grid grid-cols-5 w-full max-w-xl">
              <TabsTrigger value="overview" data-testid="tab-my-overview">
                <Target className="w-4 h-4 mr-1" />
                Sumar
              </TabsTrigger>
              <TabsTrigger value="kpis" data-testid="tab-my-kpis">
                <BarChart3 className="w-4 h-4 mr-1" />
                KPI-uri
              </TabsTrigger>
              <TabsTrigger value="badges" data-testid="tab-my-badges">
                <Trophy className="w-4 h-4 mr-1" />
                Badge-uri
              </TabsTrigger>
              <TabsTrigger value="trends" data-testid="tab-my-trends">
                <TrendingUp className="w-4 h-4 mr-1" />
                Evoluție
              </TabsTrigger>
              <TabsTrigger value="peer-review" data-testid="tab-my-peer-review">
                <Star className="w-4 h-4 mr-1" />
                Peer Review
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold text-blue-700">{employeeDetail.total_hours}</p>
                    <p className="text-sm text-blue-600">Ore lucrate</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="text-2xl font-bold text-emerald-700">{employeeDetail.tasks_completed}</p>
                    <p className="text-sm text-emerald-600">Taskuri completate</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4 text-center">
                    <Target className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                    <p className="text-2xl font-bold text-purple-700">{employeeDetail.utilization_pct}%</p>
                    <p className="text-sm text-purple-600">Utilizare</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                    <p className="text-2xl font-bold text-orange-700">{employeeDetail.projects_worked}</p>
                    <p className="text-sm text-orange-600">Proiecte</p>
                  </CardContent>
                </Card>
              </div>

              {/* Strengths and Areas for Improvement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employeeDetail.strengths?.length > 0 && (
                  <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardHeader>
                      <CardTitle className="text-emerald-600 flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        Punctele tale forte 💪
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {employeeDetail.strengths.map((s, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-emerald-700">
                            <TrendingUp className="w-4 h-4" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {employeeDetail.areas_for_improvement?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader>
                      <CardTitle className="text-amber-600 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Unde poți crește 📈
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {employeeDetail.areas_for_improvement.map((a, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-amber-700">
                            <TrendingDown className="w-4 h-4" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Motivational message based on score */}
              <Card className={`${
                employeeDetail.overall_color === 'green' ? 'bg-emerald-50 border-emerald-200' :
                employeeDetail.overall_color === 'yellow' ? 'bg-amber-50 border-amber-200' :
                'bg-blue-50 border-blue-200'
              }`}>
                <CardContent className="p-4">
                  <p className="text-center">
                    {employeeDetail.overall_color === 'green' ? (
                      <span className="text-emerald-700">🌟 Excelent! Ești în top performeri. Continuă tot așa!</span>
                    ) : employeeDetail.overall_color === 'yellow' ? (
                      <span className="text-amber-700">💪 Ești pe drumul cel bun! Cu puțin efort poți ajunge în zona verde.</span>
                    ) : (
                      <span className="text-blue-700">🚀 Fiecare zi e o oportunitate nouă. Concentrează-te pe zonele de îmbunătățire!</span>
                    )}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* KPIs Tab */}
            <TabsContent value="kpis" className="space-y-4">
              <h3 className="text-lg font-semibold">KPI-uri Detaliate</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employeeDetail.kpi_scores?.map((kpi, idx) => (
                  <KPICard key={idx} kpi={kpi} />
                ))}
              </div>

              {/* Alerts section */}
              {employeeDetail.alerts?.length > 0 && (
                <Card className="border-red-200 bg-red-50/50">
                  <CardHeader>
                    <CardTitle className="text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Atenție
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {employeeDetail.alerts.map((a, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Badges Tab */}
            <TabsContent value="badges">
              <PerformanceBadges month={selectedMonth} isAdmin={false} />
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends">
              <PerformanceTrends />
            </TabsContent>

            {/* Peer Review Tab */}
            <TabsContent value="peer-review">
              <EmployeePeerReview />
            </TabsContent>
          </Tabs>
        </div>
      </Layout>
    );
  }

  // Admin/Manager Team View
  return (
    <Layout>
      <div className="space-y-6" data-testid="team-performance-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Performanța Echipei</h1>
            <p className="text-muted-foreground">Dashboard de evaluare și KPI-uri</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline" data-testid="export-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Prezentare generală</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Angajați</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <Bell className="w-4 h-4 mr-1" />
            Notificări
          </TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges">
            <Trophy className="w-4 h-4 mr-1" />
            Badge-uri
          </TabsTrigger>
          <TabsTrigger value="peer-review" data-testid="tab-peer-review">
            <Star className="w-4 h-4 mr-1" />
            Peer Review
          </TabsTrigger>
          {selectedEmployee && (
            <TabsTrigger value="detail" data-testid="tab-detail">Detalii angajat</TabsTrigger>
          )}
          {selectedEmployee && (
            <TabsTrigger value="evaluation" data-testid="tab-evaluation">
              <ClipboardCheck className="w-4 h-4 mr-1" />
              Evaluare
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {teamReport && (
            <>
              <TeamOverview report={teamReport} />
              <StatusDistribution report={teamReport} />
              
              {teamReport.team_alerts?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-amber-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Alerte echipă
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {teamReport.team_alerts.map((alert, idx) => (
                        <li key={idx} className="text-amber-700 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          {alert}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              
              {/* Employees at Risk Section */}
              <EmployeesAtRisk 
                month={selectedMonth} 
                onViewEmployee={(empId) => {
                  fetchEmployeeDetail(empId);
                  setActiveTab('evaluation');
                }}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="employees">
          {teamReport && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamReport.employees?.map((employee) => (
                <EmployeeCard 
                  key={employee.employee_id} 
                  employee={employee} 
                  onClick={fetchEmployeeDetail}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts">
          <PerformanceAlerts currentMonth={selectedMonth} />
        </TabsContent>

        <TabsContent value="badges">
          <PerformanceBadges month={selectedMonth} isAdmin={true} />
        </TabsContent>

        <TabsContent value="peer-review">
          <AdminPeerReview />
        </TabsContent>

        <TabsContent value="detail">
          {employeeDetail && (
            <div className="space-y-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedEmployee(null);
                  setEmployeeDetail(null);
                  setActiveTab('employees');
                }}
              >
                ← Înapoi la listă
              </Button>

              {/* Overall Score Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ${getScoreColor(employeeDetail.overall_color)}`}>
                      {Math.round(employeeDetail.overall_score)}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{employeeDetail.employee_name}</h2>
                      <p className="text-muted-foreground">{employeeDetail.employee_email}</p>
                      <div className="mt-2">
                        {getScoreBadge(employeeDetail.overall_color)}
                        <span className="ml-2 text-sm text-muted-foreground">
                          Încredere: {employeeDetail.confidence_score}%
                        </span>
                      </div>
                    </div>
                    {employeeDetail.rank_in_team && (
                      <div className="ml-auto text-center">
                        <p className="text-3xl font-bold text-primary">#{employeeDetail.rank_in_team}</p>
                        <p className="text-sm text-muted-foreground">din {employeeDetail.total_team_members}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold">{employeeDetail.total_hours}</p>
                    <p className="text-sm text-muted-foreground">Ore lucrate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="text-2xl font-bold">{employeeDetail.tasks_completed}</p>
                    <p className="text-sm text-muted-foreground">Taskuri completate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Target className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                    <p className="text-2xl font-bold">{employeeDetail.utilization_pct}%</p>
                    <p className="text-sm text-muted-foreground">Utilizare</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                    <p className="text-2xl font-bold">{employeeDetail.projects_worked}</p>
                    <p className="text-sm text-muted-foreground">Proiecte</p>
                  </CardContent>
                </Card>
              </div>

              {/* KPI Breakdown */}
              <div>
                <h3 className="text-lg font-semibold mb-4">KPI-uri Detaliate</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {employeeDetail.kpi_scores?.map((kpi, idx) => (
                    <KPICard key={idx} kpi={kpi} />
                  ))}
                </div>
              </div>

              {/* Strengths and Improvements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employeeDetail.strengths?.length > 0 && (
                  <Card className="border-emerald-200">
                    <CardHeader>
                      <CardTitle className="text-emerald-600 flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        Puncte forte
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {employeeDetail.strengths.map((s, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-emerald-700">
                            <TrendingUp className="w-4 h-4" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {employeeDetail.areas_for_improvement?.length > 0 && (
                  <Card className="border-amber-200">
                    <CardHeader>
                      <CardTitle className="text-amber-600 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Zone de îmbunătățire
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {employeeDetail.areas_for_improvement.map((a, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-amber-700">
                            <TrendingDown className="w-4 h-4" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Evaluation Tab - Admin Only */}
        <TabsContent value="evaluation">
          {selectedEmployee && (
            <div className="space-y-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setActiveTab('detail');
                }}
              >
                ← Înapoi la detalii
              </Button>
              
              <EmployeeEvaluation 
                employeeId={selectedEmployee} 
                month={selectedMonth}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
}
